/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useCallback, useState, useMemo } from "react";
import MainLayout from "../../../layouts/MainLayout";
import { useNavigate, useParams } from "react-router-dom";
import "../../../assets/styles/custom-styles.css";
import useLoader from "../../../hooks/useLoader";
import useForm from "../../../hooks/useForm";
import Form from "../../../components/forms/Form";
import { editProductFields } from "../../../constants/forms/almoxarifado/productFields";
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../utils/objectUtils";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import SimpleBody from "../../../components/forms/SimpleBody";
import SectionHeader from "../../../components/forms/SectionHeader";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useSelectOptions from "../../../hooks/useSelectOptions";
import { generateProductName } from "../../../utils/generateProductName";
import { transformFiltersForUrl } from "../../../utils/transformFiltersForUrl";
import useUrlFilters from "../../../hooks/useUrlFilters";
import useRemoteSelectOptions from "../../../hooks/useRemoteSelectOptions";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";

const EditProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoader();
  const queryClient = useQueryClient();
  const {
    get: baseServiceGet,
    getByColumn: baseServiceGetByColumn,
    put: baseServicePut,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);

  // URL filters (mantidos conforme seu original)
  const { filters, pagination } = useUrlFilters(
    {
      id: [],
      name: [],
      "items.condition.name": [],
      "items.category.name": [],
      "items.address.street": [],
      "items.address.alias": [],
      "items.address.number": [],
      "items.address.city": [],
      "items.address.state": [],
      deleted_at: false,
    },
    "almox_products",
  );

  // Form state
  const { formData, handleChange, resetForm, setFormData } = useForm(
    setDefaultFieldValues(editProductFields),
    editProductFields,
  );

  // Estados locais para controlar nome e seleções
  const [productName, setProductName] = useState("");
  const [currentType, setCurrentType] = useState("");
  const [currentGroup, setCurrentGroup] = useState("");
  const [currentBrand, setCurrentBrand] = useState("");

  // Busca do produto
  const { data: product, isLoading: isLoadingProduct } = useQueryWithLoader({
    queryKey: ["product", id],
    queryFn: () =>
      baseServiceGetByColumn(entities.products.getById(id)).then(
        (res) => res.result,
      ),
    enabled: !!id,
    staleTime: Infinity,
  });

  // Select options remotas
  const typeSelect = useRemoteSelectOptions({
    queryKey: ["typeOptions"],
    entityPath: entities.types.get,
    baseServiceGet,
    fieldId: "product.type_id",
    formData,
    isMulti: false,
    immediateFetch: true,
    perPage: 99999,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (t) => ({ value: t.id, label: t.name }),
  });

  const groupsSelect = useRemoteSelectOptions({
    fieldId: "product.group_id",
    queryKey: ["groupsOptions"],
    entityPath: entities.groups.get,
    baseServiceGet,
    mapToOption: (g) => ({ value: g.id, label: g.name }),
    immediateFetch: true,
    formData,
    isMulti: false,
    perPage: 99999,
    initialPage: 1,
    enablePagination: true,
    setFormData,
  });

  const brandSelect = useRemoteSelectOptions({
    queryKey: ["brandOptions"],
    entityPath: entities.brands.get,
    baseServiceGet,
    fieldId: "product.brand_id",
    formData,
    isMulti: false,
    mapToOption: (b) => ({ value: b.id, label: b.name }),
    setFormData,
    perPage: 99999,
    initialPage: 1,
    enablePagination: true,
    immediateFetch: true,
  });

  const isPatrimonyOptionsRaw = useMemo(
    () => [
      { id: true, name: "Sim" },
      { id: false, name: "Não" },
    ],
    [],
  );

  const assetSelect = useSelectOptions({
    fieldId: "product.is_asset",
    formData,
    rawOptions: isPatrimonyOptionsRaw,
    isMulti: false,
    mapToOption: (i) => ({ value: i.id, label: i.name }),
    setFormData,
  });

  // Consolidado para fácil acesso
  const selects = {
    "product.type_id": typeSelect,
    "product.group_id": groupsSelect,
    "product.brand_id": brandSelect,
    "product.is_asset": assetSelect,
  };

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [selects],
  );

  // Inicializa formData e estados locais ao carregar o produto
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!product || isInitialized) return;

    setFormData({
      product: {
        ...product,
        is_asset: product.is_asset
          ? { value: true, label: "Sim" }
          : { value: false, label: "Não" },
      },
    });

    setCurrentType(
      typeSelect.options.find((o) => o.value === product.type_id)?.label || "",
    );
    setCurrentGroup(
      groupsSelect.options.find((o) => o.value === product.group_id)?.label ||
        "",
    );
    setCurrentBrand(
      brandSelect.options.find((o) => o.value === product.brand_id)?.label ||
        "",
    );
    setProductName(product.name);

    setIsInitialized(true); // evita reexecução
  }, [
    product,
    setFormData,
    typeSelect.options,
    groupsSelect.options,
    brandSelect.options,
    isInitialized,
  ]);

  // Atualiza os estados locais quando selects mudam (marca, tipo, grupo)
  useEffect(() => {
    if (typeSelect.selectedValue?.value) {
      setCurrentType(typeSelect.selectedValue.label || "");
    }
  }, [typeSelect.selectedValue]);

  useEffect(() => {
    if (groupsSelect.selectedValue?.value) {
      setCurrentGroup(groupsSelect.selectedValue.label || "");
    }
  }, [groupsSelect.selectedValue]);

  useEffect(() => {
    if (brandSelect.selectedValue?.value) {
      setCurrentBrand(brandSelect.selectedValue.label || "");
    }
  }, [brandSelect.selectedValue]);

  // Atualiza o nome do produto quando marca, tipo ou grupo mudam
  useEffect(() => {
    if (!currentType && !currentGroup && !currentBrand) {
      setProductName("");
      return;
    }

    const newName = generateProductName({
      type: currentType,
      group: currentGroup,
      brand: currentBrand,
    });

    setProductName(newName);
  }, [currentType, currentGroup, currentBrand]);

  // Atualiza o formData com o nome do produto gerado automaticamente
  useEffect(() => {
    if (productName) {
      handleChange("product.name", productName);
    }
  }, [productName, handleChange]);

  // Mutation para atualizar o produto
  const updateProduct = useMutation({
    mutationFn: async (data) => {
      showLoader();
      const response = await baseServicePut(entities.products.update(id), data);
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        hideLoader();
        resetForm();
        navigate("/almoxarifado/produtos");
        queryClient.invalidateQueries([
          "almox_products",
          transformFiltersForUrl(filters),
          pagination,
        ]);
      }
    },
    onError: (error) => {
      console.log("onError ", error);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  // Submit handler
  const handleSubmit = async () => {
    try {
      const cleanedProduct = cleanPayload(formData);
      const payload = {
        product: {
          ...cleanedProduct.product,
          code: String(cleanedProduct.product.code),
        },
      };

      await updateProduct.mutateAsync(payload);
    } catch (error) {
      console.error("Erro ao enviar produto:", {
        status: error.status,
        message: error.message,
      });
    }
  };

  // Navegação
  const handleBack = () => navigate("/almoxarifado/produtos/");

  // Loader global
  useEffect(() => {
    if (isLoadingProduct) showLoader();
    else hideLoader();
  }, [isLoadingProduct, showLoader, hideLoader]);

  // Helpers para selects usados no Form
  const getOptions = useCallback(
    (fieldId) => selects[fieldId]?.options || [],
    [selects],
  );
  const getSelectedValue = useCallback(
    (fieldId) => selects[fieldId]?.selectedValue || null,
    [selects],
  );

  return (
    <MainLayout>
      <PageHeader
        title="Editar Produto"
        showBackButton
        backUrl="/almoxarifado/produtos/"
      />
      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          onSubmit={handleSubmit}
          textSubmit="Editar Produto"
          textLoadingSubmit="Editando..."
          handleBack={handleBack}
          isLoading={isLoadingProduct || updateProduct.isPending}
        >
          {() => {
            const renderedSections = new Set();

            return editProductFields.map((section, index) => {
              const sectionType = section.section.split(" - ")[0];
              const showHeader = !renderedSections.has(sectionType);
              if (showHeader) renderedSections.add(sectionType);

              return (
                <div key={`${section.section}-${index}`}>
                  {showHeader && (
                    <SectionHeader
                      section={section.section}
                      viewTable={false}
                      setViewTable={() => {}}
                      addFieldsInData={() => {}}
                    />
                  )}
                  <SimpleBody
                    fields={section.fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={handleChange}
                    getOptions={getOptions}
                    getSelectedValue={getSelectedValue}
                    getSelectMeta={getSelectMeta}
                  />
                </div>
              );
            });
          }}
        </Form>
      </div>
    </MainLayout>
  );
};

export default EditProductPage;
